import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePublicCatalogData } from '@/components/PublicCatalogData';
import { regionsService } from '../api/services';

const DEFAULT_TTL_MS = 2 * 60 * 1000;
const cache = new Map();

const buildKey = (params) => {
  if (!params || Object.keys(params).length === 0) {
    return 'regions:list';
  }
  return `regions:list:${JSON.stringify(params)}`;
};

const extractData = (response) => {
  if (!response) return null;
  return response.data?.data || response.data || [];
};

const shouldRevalidate = (entry, ttlMs) => {
  if (!entry || !entry.data) return true;
  return Date.now() - entry.timestamp > ttlMs;
};

const useRegionsList = (params = null, options = {}) => {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const catalog = usePublicCatalogData();
  const contextList = !params && catalog?.regions != null ? catalog.regions : null;
  const cacheKey = useMemo(() => buildKey(params), [params]);
  const cached = cache.get(cacheKey);
  const [response, setResponse] = useState(() => {
    if (contextList != null) return { data: { data: contextList, success: true } };
    return cached?.data ?? null;
  });
  const [loading, setLoading] = useState(contextList == null && !cached?.data);
  const [error, setError] = useState(null);

  const fetchAndUpdate = useCallback(
    (force = false) => {
      const entry = cache.get(cacheKey);
      if (!force && entry?.inFlight) {
        entry.inFlight
          .then((res) => {
            setResponse(res);
            setError(null);
            setLoading(false);
            return res;
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
            throw err;
          });
        return entry.inFlight;
      }

      const request = regionsService.getAll(params);
      const nextEntry = entry || { data: null, timestamp: 0, inFlight: null };
      nextEntry.inFlight = request;
      cache.set(cacheKey, nextEntry);

      request
        .then((res) => {
          cache.set(cacheKey, { data: res, timestamp: Date.now(), inFlight: null });
          setResponse(res);
          setError(null);
          setLoading(false);
          return res;
        })
        .catch((err) => {
          const existing = cache.get(cacheKey) || { data: null, timestamp: 0, inFlight: null };
          existing.inFlight = null;
          cache.set(cacheKey, existing);
          setError(err);
          setLoading(false);
          throw err;
        });

      return request;
    },
    [cacheKey, params]
  );

  useEffect(() => {
    let active = true;
    if (contextList != null) {
      setResponse({ data: { data: contextList, success: true } });
      setLoading(false);
      return () => {
        active = false;
      };
    }
    const entry = cache.get(cacheKey);

    if (entry?.data) {
      setResponse(entry.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (shouldRevalidate(entry, ttlMs)) {
      fetchAndUpdate().catch(() => {
        if (!active) return;
      });
    }

    return () => {
      active = false;
    };
  }, [cacheKey, ttlMs, fetchAndUpdate, contextList]);

  const data = useMemo(() => extractData(response), [response]);

  return {
    response,
    data,
    loading,
    error,
    refresh: () => fetchAndUpdate(true)
  };
};

export default useRegionsList;
